import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, CreditCard, Shield, CheckCircle, Lock,
  DollarSign, Loader2, AlertCircle, Receipt
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentPage() {
  const { escrowId } = useParams();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [escrow, setEscrow] = useState(null);
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, [escrowId, token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const escrowRes = await fetch(`${API_URL}/api/escrows/${escrowId}`, { headers });
      if (escrowRes.ok) {
        const escrowData = await escrowRes.json();
        setEscrow(escrowData);
        
        const contractRes = await fetch(`${API_URL}/api/contracts/${escrowData.contract_id}`, { headers });
        if (contractRes.ok) {
          setContract(await contractRes.json());
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados do pagamento");
    } finally {
      setLoading(false);
    }
  };

  const initiatePayment = async (amount) => {
    setProcessing(true);
    try {
      // Create payment intent
      const intentRes = await fetch(`${API_URL}/api/payments/create-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          escrow_id: escrowId,
          amount: amount
        })
      });

      if (!intentRes.ok) {
        throw new Error("Erro ao criar pagamento");
      }

      const { client_secret, payment_intent_id } = await intentRes.json();

      // In production, we would use Stripe.js here
      // For now, simulate payment confirmation
      toast.info("Processando pagamento...");
      
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Confirm payment
      const confirmRes = await fetch(`${API_URL}/api/payments/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          payment_intent_id: payment_intent_id,
          escrow_id: escrowId
        })
      });

      if (confirmRes.ok) {
        const result = await confirmRes.json();
        if (result.status === "success") {
          setPaymentSuccess(true);
          toast.success("Pagamento realizado com sucesso!");
          fetchData();
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!escrow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Card className="p-6 text-center">
          <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Escrow não encontrado</p>
          <Button className="mt-4" onClick={() => navigate("/contracts")}>
            Voltar
          </Button>
        </Card>
      </div>
    );
  }

  const amountDue = escrow.total_amount - (escrow.deposited_amount || 0);
  const signalAmount = contract?.signal_amount || escrow.total_amount * 0.3;
  const isFunded = escrow.status === "funded" || escrow.deposited_amount >= escrow.total_amount;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="payment-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/contracts")} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <span className="font-semibold text-slate-900">Pagamento</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Payment Success */}
        {paymentSuccess && (
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-emerald-800 mb-2">Pagamento Confirmado!</h2>
              <p className="text-emerald-600 mb-4">
                Seu depósito foi realizado com sucesso e os fundos estão protegidos em escrow.
              </p>
              <Button onClick={() => navigate(`/execution/${contract?.contract_id}`)}>
                Acompanhar Execução
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Escrow Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Resumo do Escrow
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Valor Total do Contrato</span>
              <span className="text-xl font-bold text-slate-900">
                R$ {escrow.total_amount?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Já Depositado</span>
              <span className="text-lg font-semibold text-emerald-600">
                R$ {(escrow.deposited_amount || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-slate-600">Liberado ao Prestador</span>
              <span className="text-lg font-semibold text-blue-600">
                R$ {(escrow.released_amount || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </span>
            </div>
            {contract?.retention_percentage > 0 && (
              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <span className="text-slate-600">Retenção ({(contract.retention_percentage * 100).toFixed(0)}%)</span>
                <span className="text-lg font-semibold text-amber-600">
                  R$ {(escrow.total_amount * contract.retention_percentage).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between py-3 bg-slate-50 rounded-lg px-4">
              <span className="font-semibold text-slate-900">A Pagar</span>
              <span className="text-2xl font-bold text-slate-900">
                R$ {Math.max(0, amountDue).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </span>
            </div>
            
            <Badge className={
              escrow.status === "funded" ? "bg-emerald-100 text-emerald-700" :
              escrow.status === "partial" ? "bg-amber-100 text-amber-700" :
              "bg-slate-100 text-slate-700"
            }>
              Status: {
                escrow.status === "funded" ? "Totalmente Financiado" :
                escrow.status === "partial" ? "Parcialmente Financiado" :
                escrow.status === "pending" ? "Aguardando Depósito" :
                escrow.status
              }
            </Badge>
          </CardContent>
        </Card>

        {/* Payment Options */}
        {!isFunded && user?.role === "client" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Opções de Pagamento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Signal Payment */}
              {escrow.deposited_amount === 0 && (
                <div className="p-4 border-2 border-emerald-200 rounded-lg bg-emerald-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-emerald-800">Sinal (30%)</h4>
                      <p className="text-sm text-emerald-600">Pague o sinal para iniciar o serviço</p>
                    </div>
                    <span className="text-xl font-bold text-emerald-700">
                      R$ {signalAmount.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => initiatePayment(signalAmount)}
                    disabled={processing}
                    data-testid="pay-signal-btn"
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    Pagar Sinal
                  </Button>
                </div>
              )}

              {/* Full Payment */}
              <div className="p-4 border-2 border-slate-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-slate-900">Pagamento Total</h4>
                    <p className="text-sm text-slate-500">Pague o valor total restante</p>
                  </div>
                  <span className="text-xl font-bold text-slate-900">
                    R$ {amountDue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <Button 
                  className="w-full"
                  onClick={() => initiatePayment(amountDue)}
                  disabled={processing || amountDue <= 0}
                  data-testid="pay-full-btn"
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Pagar Valor Total
                </Button>
              </div>

              {/* Trust Banner */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <Shield className="w-10 h-10 text-slate-600" />
                <div>
                  <p className="font-medium text-slate-900">Pagamento 100% Seguro</p>
                  <p className="text-sm text-slate-500">
                    Seus fundos ficam protegidos em escrow até você aprovar o serviço
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transaction History */}
        {escrow.transactions?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Histórico de Transações
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {escrow.transactions.map((txn) => (
                  <div 
                    key={txn.transaction_id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      txn.type === "deposit" ? "bg-emerald-50" : "bg-blue-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        txn.type === "deposit" ? "bg-emerald-100" : "bg-blue-100"
                      }`}>
                        {txn.type === "deposit" ? (
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {txn.type === "deposit" ? "Depósito" : "Liberação"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(txn.created_at).toLocaleString("pt-BR")}
                        </p>
                      </div>
                    </div>
                    <span className={`font-bold ${
                      txn.type === "deposit" ? "text-emerald-600" : "text-blue-600"
                    }`}>
                      {txn.type === "deposit" ? "+" : "-"} R$ {txn.amount.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security Info */}
        <Card className="bg-slate-900 text-white">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Lock className="w-10 h-10 text-emerald-400 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-lg mb-2">Sistema de Escrow eZelo.</h3>
                <ul className="text-slate-300 text-sm space-y-2">
                  <li>• Seu dinheiro fica protegido até você aprovar o serviço</li>
                  <li>• Liberação automática após aprovação</li>
                  <li>• Retenção de garantia para serviços de reforma</li>
                  <li>• Disputa mediada por IA em caso de problemas</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
